import { useState, useMemo } from 'react';

const MONTHS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'] as const;

export type MonthFilter = number | 'todos';

export interface DateRange {
  start: Date;
  end: Date;
}

export function buildMonthRange(monthIndex: number): DateRange {
  const year = new Date().getFullYear();
  const start = new Date(year, monthIndex, 1);
  // Día 0 del mes siguiente = último día del mes actual
  const end = new Date(year, monthIndex + 1, 0);
  return { start, end };
}

export function buildYearRange(year: number): DateRange {
  return { start: new Date(year, 0, 1), end: new Date(year, 11, 31) };
}

function isCurrentYearRange(range: DateRange): boolean {
  const { start, end } = range;
  const year = new Date().getFullYear();
  const expected = buildYearRange(year);
  return (
    start.getFullYear() === year &&
    start.getMonth() === expected.start.getMonth() &&
    start.getDate() === expected.start.getDate() &&
    end.getFullYear() === year &&
    end.getMonth() === expected.end.getMonth() &&
    end.getDate() === expected.end.getDate()
  );
}

function deriveActiveMonth(range: DateRange | null): MonthFilter {
  if (!range) return 'todos';
  // "Todos" ahora representa el año actual completo.
  if (isCurrentYearRange(range)) return 'todos';

  const { start, end } = range;
  const monthIndex = start.getMonth();
  const year = start.getFullYear();
  // Verificar que start sea día 1 del mes y end sea el último día del mismo mes/año
  const expected = buildMonthRange(monthIndex);
  if (
    start.getFullYear() === year &&
    start.getDate() === 1 &&
    end.getFullYear() === year &&
    end.getMonth() === monthIndex &&
    end.getDate() === expected.end.getDate()
  ) {
    return monthIndex;
  }
  // Rango personalizado: ningún tab resaltado — usamos null pero el tipo es MonthFilter,
  // así que devolvemos 'todos' solo si no hay rango; acá ya hay rango, se mapea a un
  // número inválido para que ningún botón quede activo. Se prefiere retornar 'todos'
  // para que al menos el botón "Todos" no quede activo cuando hay rango personalizado.
  // Usamos un valor especial: -1 (número fuera del rango 0-11).
  return -1;
}

/**
 * @param initialRange Rango inicial del filtro. Por defecto `null` (sin filtro /
 * "todos"), que es el comportamiento histórico usado por Cobros Pendientes y
 * Egresos Pendientes. El resumen de contabilidad pasa explícitamente el mes
 * actual para arrancar filtrado.
 */
export function useDateRangeFilter(initialRange: DateRange | null = null) {
  const [range, setRange] = useState<DateRange | null>(initialRange);

  const activeMonth = useMemo(() => deriveActiveMonth(range), [range]);

  const selectMonth = (monthIndex: number) => {
    setRange(buildMonthRange(monthIndex));
  };

  const selectRange = (newRange: DateRange | null) => {
    setRange(newRange);
  };

  // "Todos" ahora significa: todos los registros del año actual.
  const selectAll = () => {
    setRange(buildYearRange(new Date().getFullYear()));
  };

  const clearFilter = () => {
    setRange(null);
  };

  return { range, activeMonth, selectMonth, selectRange, selectAll, clearFilter, MONTHS };
}
